import React, { useEffect, useContext } from 'react';
import { string, func, arrayOf, bool } from 'prop-types';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { ExperienceContext, useStoreId } from '~/@thd-nucleus/experience-context';
import { useProductAddOnsFlow } from '../hooks/useProductAddOnsFlow';
import { HomeDepotProtectionPlan } from './add-ons/hdpp/HomeDepotProtectionPlan';
import { AttachLabor } from './add-ons/attach-labor/AttachLabor';
import { ProReferral } from './add-ons/ProReferral/ProReferral';
import { DualPath } from './add-ons/dual-path/DualPath';
import { SAMDualPath } from './add-ons/SAM-dual-path/SAMDualPath';
import { displayAttachLabor as attachLaborEligible } from '../utils/product-add-on-utils';
import { dataModel } from './dataModel';
import './product-add-ons.scss';
import './tooltip.scss';

const ProductAddOns = ({
  itemId, fulfillment, availableFulfillments, onChange
}) => {

  const expData = useContext(ExperienceContext);
  const { channel } = expData;

  const storeId = useStoreId();

  let {
    error,
    loading,
    data,
    isProtectionPlan,
    isProReferral,
    isAssembly,
    isDualPath,
    isInstallationSku,
    inStoreAssemblyAvailable,
    isSAMDualPath
  } = useProductAddOnsFlow({ itemId, storeId });

  const getCategory = (serviceName) => {
    const PR_INSTALL = 'PRInstall';
    const PAINTER = 'Painter';
    const ASSEMBLY = 'Assembly';

    if (serviceName === 'interior_painting') return PAINTER;
    if (serviceName === 'barbeque_assembly' || serviceName === 'outdoor_furniture_assembly') return ASSEMBLY;

    return PR_INSTALL;
  };

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-add-ons.ready'); }, []);

  if (!data || loading || (error && !data)) {
    return null;
  }

  if (!data?.protectionPlan && !data?.serviceAddOns && !isSAMDualPath) {
    return null;
  }

  const { price, termLength, detailsUrl, itemId: protectionPlanItemId } = data?.protectionPlan || {};

  const { installationProgramCategory } = data?.serviceAddOns?.sellFurnishInstall || {};
  const { serviceName } = data?.serviceAddOns?.proReferrals?.[0] || {};
  const { attachLabor } = data?.serviceAddOns || {};
  const installSkus = attachLabor?.installSkus?.[0] || {};
  const { serviceType, installSku } = installSkus || {};
  const { price: installRetailPrice } = attachLabor?.installSkus?.[0]?.installRetailPrice || {};
  const { installProcess, priceIncludes } = attachLabor?.installSkus?.[0]?.doItForMe || {};
  const modelNumber = data?.product?.identifiers?.modelNumber || {};

  const type = serviceType?.toLowerCase();
  const displayAttachLabor = attachLaborEligible({
    isInstallationSku,
    inStoreAssemblyAvailable,
    availableFulfillments
  });

  return (
    <section className="product-add-ons">
      <LayoutManager name="product-add-ons" useContextForProps>

        {displayAttachLabor && (
          <AttachLabor
            channel={channel}
            onChange={onChange}
            itemId={installSku}
            type={type}
            price={installRetailPrice}
            installProcess={installProcess}
            priceIncludes={priceIncludes}
            fulfillment={fulfillment}
            availableFulfillments={availableFulfillments}
            isInstallationSku={isInstallationSku}
            inStoreAssemblyAvailable={inStoreAssemblyAvailable}
          />
        )}

        {isDualPath && (
          <DualPath
            channel={channel}
            onChange={onChange}
            itemId={itemId}
            category={installationProgramCategory}
          />
        )}

        {isSAMDualPath && (
          <SAMDualPath
            channel={channel}
            onChange={onChange}
            itemId={itemId}
            modelNumber={modelNumber}
          />
        )}

        {(!displayAttachLabor && (isProReferral || isAssembly)) && (
          <ProReferral
            onChange={onChange}
            channel={channel}
            category={getCategory(serviceName)}
            installType={installationProgramCategory}
            isAssembly={isAssembly}
          />
        )}

        <LayoutGroup name="hdpp">
          {isProtectionPlan && (
            <HomeDepotProtectionPlan
              channel={channel}
              onChange={onChange}
              itemId={protectionPlanItemId}
              price={price}
              term={termLength}
              url={detailsUrl}
            />
          )}
        </LayoutGroup>
      </LayoutManager>
    </section>
  );
};

ProductAddOns.displayName = 'ProductAddOns';

ProductAddOns.defaultProps = {
  availableFulfillments: []
};
ProductAddOns.propTypes = {
  /** Product Number */
  itemId: string.isRequired,
  /** Function to call when checkbox is clicked */
  onChange: func.isRequired,
  /** Fulfillment method selected */
  fulfillment: string.isRequired,
  /** List of currently available fulfillment types  */
  availableFulfillments: arrayOf(string)
};

ProductAddOns.dataModel = dataModel;

export { ProductAddOns };
